import './company-verification-page.scss';
import { Loader, LoadDocBtn, IconBtn } from '@ru-digital-assets/ui-component';
import { useParams } from 'react-router';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'hooks/redux';
import OperatorAPI from 'services/api/Operator';
import { setPush } from 'store/actions/app';
import PageCard from 'containers/PageCard';
import { useHistory } from 'react-router';
import PageHeader from 'modules/page-header';
import cn from 'classnames';
import { showLoader, hideLoader } from 'store/actions/loader';
import {
  ICompanyVerificationInfo,
  CompanyDocumentType,
  ICompanyDocument,
} from 'constants/company-verification';
import { downloadFile } from 'utils/files/downloadFile';
import { CheckmarkIcon } from 'assets/icons';
import { CompanyVerificationModal } from './components';

export default function CompanyVerificationPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const token = useSelector((s) => s.auth.account.token);
  const [companyInfo, setCompanyInfo] = useState<ICompanyVerificationInfo>();
  const availableDocuments = useRef(
    {} as Record<CompanyDocumentType, ICompanyDocument>
  );

  const [verificationModalOpen, setVerificationModalOpen] = useState(false);

  const updateComapanyInfo = useCallback(
    (callback?: () => void) => {
      OperatorAPI.getVerification(token, +id)
        .then((response) => {
          if (response.data.documents) {
            for (const document of response.data.documents) {
              availableDocuments.current[document.type] = document;
            }
          }
          setCompanyInfo(response.data);
        })
        .catch(() => {
          dispatch(
            setPush({
              type: 'error',
              message: 'Не удалось загрузить информацию о компании',
            })
          );
        })
        .finally(callback);
    },
    [id]
  );

  useEffect(() => {
    updateComapanyInfo();
  }, [updateComapanyInfo]);

  const onClose = useCallback(() => history.push('/history'), []);

  const isDocAvailable = useCallback((docType: CompanyDocumentType) => {
    return docType in availableDocuments.current;
  }, []);

  const onDownloadDoc = useCallback(
    (docType: CompanyDocumentType, fileName: string, mimeType: string) => {
      dispatch(showLoader());
      OperatorAPI.getVerificationDocument(
        token,
        availableDocuments.current[docType].organizationId,
        docType
      )
        .then((response) => {
          downloadFile(response.data, fileName, mimeType);
        })
        .catch(() => {
          dispatch(
            setPush({
              type: 'error',
              message: 'Не удалось загрузить файл',
            })
          );
        })
        .finally(() => {
          dispatch(hideLoader());
        });
    },
    []
  );

  const onSubmitVerification = useCallback(() => {
    setVerificationModalOpen(false);
    dispatch(showLoader());
    OperatorAPI.approveVerification(token, {
      orgId: companyInfo!.organizationId,
    })
      .then(() => {
        updateComapanyInfo(() => {
          dispatch(hideLoader());
          dispatch(
            setPush({
              type: 'success',
              message: 'Данные Пользователя подтверждены',
            })
          );
        });
      })
      .catch(() => {
        dispatch(
          setPush({
            type: 'error',
            message: 'Не удалось подтвердить данные компании',
          })
        );
      })
      .finally(() => {
        dispatch(hideLoader());
      });
  }, [companyInfo]);

  return (
    <div className="company-verification-page">
      <PageHeader
        items={[
          { link: '/history', title: 'Операции' },
          { link: '', title: 'Подтверждение данных компаний' },
        ]}
        iconType="history"
        isHideBalanceInformer
      />
      {companyInfo ? (
        <PageCard
          title="Подтверждение данных Пользователя"
          onClose={onClose}
          minHeight="calc(100vh - 102px)"
          main={
            <div className="company-verification-page__main">
              <div className="company-verification-page__info">
                {companyInfo.isPersonal ?
                  (<p className="p p_bold mb_16">Физическое лицо</p>) :
                  (<p className="p p_bold mb_16">Юридическое лицо</p>)}

                <p className="p p_font-size_18 mb_32">
                  {companyInfo.organizationName}
                </p>
                <p className="p p_bold mb_16">Статус:</p>
                <div
                  className={cn('company-verification-page__status', 'mb_32', {
                    'company-verification-page__status_yellow': !companyInfo.verified,
                    'company-verification-page__status_green':
                      companyInfo.verified,
                  })}
                >
                  {companyInfo.verified
                    ? 'Подтверждена'
                    : 'Ожидание подтверждения'}
                </div>
                {companyInfo.documents && (
                  <>
                    <p className="p p_bold mb_24">Документы:</p>
                    {isDocAvailable(
                      CompanyDocumentType.AUTHORITY_CONFIRMATION
                    ) && (
                      <div className="mb_16">
                        <LoadDocBtn
                          title="Документ подтверждающий полномочия"
                          extension=".PDF"
                          onSpecDownload={() =>
                            onDownloadDoc(
                              CompanyDocumentType.AUTHORITY_CONFIRMATION,
                              'Документ подтверждающий полномочия',
                              'application/pdf'
                            )
                          }
                        />
                      </div>
                    )}
                    {isDocAvailable(CompanyDocumentType.PASSPORT) && (
                      <div className="mb_16">
                        <LoadDocBtn
                          title="Документ удостоверяющий личность"
                          extension=".PDF"
                          onSpecDownload={() =>
                            onDownloadDoc(
                              CompanyDocumentType.PASSPORT,
                              'Документ удостоверяющий личность',
                              'application/pdf'
                            )
                          }
                        />
                      </div>
                    )}
                    {isDocAvailable(CompanyDocumentType.TIN) && (
                      <div className="mb_16">
                        <LoadDocBtn
                          title="Свидетельство ИНН компании"
                          extension=".PDF"
                          onSpecDownload={() =>
                            onDownloadDoc(
                              CompanyDocumentType.TIN,
                              'Свидетельство ИНН компании',
                              'application/pdf'
                            )
                          }
                        />
                      </div>
                    )}
                    {isDocAvailable(
                      CompanyDocumentType.PERSONAL_DATA_AGREEMENT
                    ) && (
                      <div className="mb_16">
                        <LoadDocBtn
                          title="Согласие на обработку персональных данных"
                          extension=".PDF"
                          onSpecDownload={() =>
                            onDownloadDoc(
                              CompanyDocumentType.PERSONAL_DATA_AGREEMENT,
                              'Согласие на обработку персональных данных',
                              'application/pdf'
                            )
                          }
                        />
                      </div>
                    )}
                    {isDocAvailable(
                      CompanyDocumentType.ONBOARDING_DOCUMENT
                    ) && (
                      <div className="mb_16">
                        <LoadDocBtn
                          title="Договор о присоединении"
                          extension=".PDF"
                          onSpecDownload={() =>
                            onDownloadDoc(
                              CompanyDocumentType.ONBOARDING_DOCUMENT,
                              'Договор о присоединении',
                              'application/pdf'
                            )
                          }
                          secondExtension={
                            isDocAvailable(
                              CompanyDocumentType.SIGNED_ONBOARDING_DOCUMENT
                            )
                              ? '.SIG'
                              : undefined
                          }
                          onSecondSpecDownload={
                            isDocAvailable(
                              CompanyDocumentType.SIGNED_ONBOARDING_DOCUMENT
                            )
                              ? () =>
                                  onDownloadDoc(
                                    CompanyDocumentType.SIGNED_ONBOARDING_DOCUMENT,
                                    'Договор о присоединении',
                                    'application/pgp-signature'
                                  )
                              : undefined
                          }
                        />
                      </div>
                    )}
                    {/* <div>
                      <LoadDocBtn
                        title="Скачать одним архивом"
                        extension=".ZIP"
                        onSpecDownload={() => {}}
                      />
                    </div> */}
                  </>
                )}
              </div>
              <div className="company-verification-page__sidebar">
                <p className="p p_bold mb_24">Данные Пользователя:</p>
                <p className="p p_font-size_12 p_color_light-grey mb_2">ФИО</p>
                <p className="p mb_12">{companyInfo.initiatorFullName}</p>
                {!companyInfo.isPersonal && (
                  <>
                    <p className="p p_font-size_12 p_color_light-grey mb_2">
                      Должность
                    </p>
                    <p className="p mb_12">{companyInfo.initiatorRank}</p>
                  </>)
                }
                <p className="p p_font-size_12 p_color_light-grey mb_2">
                  Телефон
                </p>
                <p className="p mb_12">{companyInfo.initiatorPhoneNumber}</p>
                <p className="p p_font-size_12 p_color_light-grey mb_2">
                  Email
                </p>
                <p className="p mb_32">{companyInfo.initiatorEmail}</p>
                {!companyInfo.isPersonal && (
                  <>
                    <p className="p p_bold mb_24">Данные компании:</p>
                    <p className="p p_font-size_12 p_color_light-grey mb_2">
                      Полное наименование компании
                    </p>
                    <p className="p mb_12">{companyInfo.organizationFullName}</p>
                    <p className="p p_font-size_12 p_color_light-grey mb_2">
                      Сокращенное наименование компании
                    </p>
                    <p className="p mb_12">{companyInfo.organizationName}</p>
                    <p className="p p_font-size_12 p_color_light-grey mb_2">ИНН</p>
                    <p className="p mb_12">{companyInfo.tin}</p>
                    <p className="p p_font-size_12 p_color_light-grey mb_2">ОГРН</p>
                    <p className="p mb_12">{companyInfo.regNumber}</p>
                  </>
                )}
              </div>
            </div>
          }
          footer={
            companyInfo.verified ? null : (
              <div className="company-verification-page__footer">
                <CompanyVerificationModal
                  open={verificationModalOpen}
                  onClose={() => setVerificationModalOpen(false)}
                  onSubmit={onSubmitVerification}
                  companyName={companyInfo.organizationName}
                />
                <IconBtn
                  title="Подтвердить данные Пользователя"
                  icon={<CheckmarkIcon />}
                  onClick={() => setVerificationModalOpen(true)}
                />
              </div>
            )
          }
        />
      ) : (
        <div className="flex justify-center mt_32">
          <Loader />
        </div>
      )}
    </div>
  );
}
